import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Grid,
  Typography,
  Divider,
  TextField,
  Tooltip,
  Button,
  makeStyles,
  FormHelperText,
} from "@material-ui/core";
import Box from "@mui/material/Box";
import styled from "styled-components";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import draftToHtml from "draftjs-to-html";
import ReactHtmlParser from "react-html-parser";
import TextEditor from "./TextEditor.js";
import AlertDialog from "../../../components/AlertDialog.js";

import infoImg from "../../../assets/icons/info.png";

const StyledTooltip = styled(props => (
  <Tooltip
    classes={{ popper: props.className }}
    {...props}
    PopperProps={{
      popperOptions: {
        modifiers: {
          flip: { enabled: false },
          offset: {
            offset: "0px, -35px, 20px, -25px",
          },
        },
      },
    }}
  />
))`
  & .MuiTooltip-tooltip {
    display: flex;
    background: #757575;
    color: #fff;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 500;
  }
`;
const useStyles = makeStyles(theme => ({
  textFieldInput: {
    resize: "both",
    height: "200px",
    maxHeight: "350px",
    width: "500px",
    maxWidth: "550px"
  },
  outlinedButtonStyle: {
    borderRadius: 4,
    width: "100px",
    padding: 0,
    color: "#0D47A1",
    height: "34px",
    textTransform: "none",
  },
  buttonStyle: {
    borderRadius: 4,
    width: "100px",
    padding: 0,
    color: "#fff",
    height: "34px",
    backgroundColor: "#0D47A1",
    textTransform: "none",
    "&:active": {
      backgroundColor: "#0D47A1",
    },
    "&:hover": {
      backgroundColor: "#1E88E5",
    },
  },
  subDialog: {
    "& .MuiDialogContent-root": {
      padding: 0,
    },
    "& .MuiPaper-root": {
      width: "100%",
      maxWidth: "1100px",
      height: "600px",
    },
  },
  textFieldStyle: {
    marginBottom: 5,
    marginTop: 20,
    "& .MuiInputLabel-outlined": {
      fontSize: 16,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 4,
      fontSize: 16,
      backgroundColor: "#fff",
    },
  },
  textFieldOnFormStyle: {
    marginBottom: 5,
    "& .MuiInputLabel-outlined": {
      fontSize: 16,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      fontSize: 16,
      backgroundColor: "#fff",
    },
  },

  switch: {
    "& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track": {
      backgroundColor: "#0D47A1",
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: "#0D47A1",
    },
  },
  errorStyle: {
    marginTop: 0,
    fontSize: 14, //16,
    fontFamily: "Arial",
  },
}));

export default function EditTextAreaToFormDialog({
  closeDialog,
  handleDone,
  infoIcon,
  manAnswer,
  queText,
  queRaw,
  capText,
  capRaw,
  maxChar,
  infoTxt,
  width,
  height
}) {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const [infoIconEdit, setInfoIconEdit] = useState(infoIcon);
  const [manAnswerEdit, setManAnswerEdit] = useState(manAnswer);
  const [queTextEdit, setQueTextEdit] = useState(queText);
  const [queRawEdit, setQueRawEdit] = useState(queRaw);
  const [capTextEdit, setCapTextEdit] = useState(capText);
  const [capRawEdit, setCapRawEdit] = useState(capRaw);
  const [maxCharEdit, setMaxCharEdit] = useState(maxChar);
  const [infoTxtEdit, setInfoTxtEdit] = useState(infoTxt);
  const [widthEdit, setWidthEdit] = useState(width);
  const [heightEdit, setheightEdit] = useState(height);
  const [alertMsg, setAlertMsg] = useState([]);
  const [focusInfo, setFocusInfo] = useState(false);
  const [remainedChars, setRemainedChars] = useState(null);
  const [currentInputLength, setCurrentInputLength] = useState(null);

  const editorRef = useRef(null);
  const [checkError, setCheckError] = useState(false);

  const handleClose = () => {
    setOpen(false);
    closeDialog && closeDialog();
  };

  const handleChangeTextEditor = (txt, type) => {
    let contentHTML = draftToHtml(txt);
    contentHTML = contentHTML.replace(/<p>/, "");

    contentHTML = contentHTML.replace(
      /<a[^>]*href="([^"]+)"[^>]*>(.*?)<\/a>/gi,
      (match, href, text) => {
        // Remove leading protocols
        href = href.replace(/^(https?:\/\/)+/, "");

        // Prepend https://
        href = `https://${href}`;

        return `<a href="${href}" target="_blank">${text}</a>`;
      }
    );

    const convertedTxt = ReactHtmlParser(contentHTML);
    if (type === "question") {
      setQueTextEdit(convertedTxt);
      setQueRawEdit(txt);
    }
    if (type === "caption") {
      setCapTextEdit(convertedTxt);
      setCapRawEdit(txt);
    }
  };

  const validateFields = () => {
    setCheckError(true);
    if (queRawEdit.blocks[0].text.trim() === "") {
      editorRef.current.focus();
      return false;
    }
    if (infoIconEdit && infoTxtEdit.trim() === "") {
      return false;
    }
    if (!maxCharEdit || maxCharEdit.toString().trim() === "") {
      return false;
    }
    if (isNaN(parseInt(maxCharEdit)) ||
        parseInt(maxCharEdit) < 151 ||
        parseInt(maxCharEdit) > 5000) {
      return false;
    }
    if (widthEdit.toString().trim() === "") {
      return false;
    }
    if (isNaN(parseInt(widthEdit)) ||
        parseInt(widthEdit) < 30 ||
        parseInt(widthEdit) > 50) {
      return false;
    }
    if (heightEdit.toString().trim() === "") {
      return false;
    }
    if (isNaN(parseInt(heightEdit)) ||
        parseInt(heightEdit) < 3 ||
        parseInt(heightEdit) > 6) {
      return false;
    }
    return true;
  };

  const updRemainedChars = (input) => {
    setCurrentInputLength(input.length);
    maxCharEdit !== "" && setRemainedChars(maxCharEdit - input.length)
  }

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth="xl"
      //  onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className={classes.subDialog}
    >
      <DialogTitle sx={{ p: 0 }} id="alert-dialog-title">
        <Box
          style={{
            display: "flex",
            flexDirection: "row",
          }}
        >
          <Grid container>
            <Grid
              item
              lg={4}
              xl={4}
              md={4}
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                paddingBottom: 15,
              }}
            >
              <Box style={{ width: 100 }}>
                <IconButton
                  aria-label="close"
                  sx={{
                    position: "absolute",
                    left: 8,
                    top: 8,
                    align: "right",
                    color: theme => theme.palette.grey[500],
                  }}
                  onClick={e => {
                    if (
                      queRawEdit.blocks[0].text !== queRaw.blocks[0].text ||
                      capRawEdit.blocks[0].text !== capRaw.blocks[0].text ||
                      maxCharEdit !== maxChar ||
                      infoTxtEdit !== infoTxt
                    )
                      setAlertMsg({
                        title: "Exit",
                        type: "saveConfirmation",
                        msg: "Are you sure you want to exit without saving your work?",
                      });
                    else handleClose();
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              <Box pt={2} style={{ width: 350 }}>
                <Typography
                  variant="h1"
                  style={{
                    fontSize: 20,
                    display: "flex",
                    //    flexDirection: "end",
                    alignItems: "center",
                  }}
                >
                  Object: Text Area
                </Typography>
              </Box>
            </Grid>
            <Grid item lg={8} xl={8} md={8} style={{ backgroundColor: "#E3F2FD" }}></Grid>
          </Grid>
        </Box>
      </DialogTitle>
      <DialogContent
        style={{
          alignItems: "center",
          display: "flex",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <Grid container>
          <Grid item lg={4} xl={4} md={4}>
            <Card style={{ maxHeight: "550px", overflowY: "scroll" }}>
              <Box mb={1} mt={5}>
                <Divider />
                <Box mt={2} ml={2}>
                  <Typography variant="h3">Question*</Typography>
                </Box>
                <Box mt={2} ml={2} mb={2} mr={2}>
                  <TextEditor
                    type="question"
                    value={queRaw}
                    handleChangeText={(txt, type) => handleChangeTextEditor(txt, type)}
                    editorRef={editorRef}
                  />
                  {checkError &&
                    queRawEdit &&
                    queRawEdit.blocks[0].text.trim() === "" && (
                      <FormHelperText className={classes.errorStyle} error>
                        Question field cannot be blank.
                      </FormHelperText>
                    )}
                </Box>
                <Divider />
                <Box mt={2} ml={2}>
                  <Typography variant="h3">Caption</Typography>
                </Box>
                <Box mt={2} ml={2} mb={2} mr={2}>
                  <TextEditor
                    type="caption"
                    maxLength = {600}
                    value={capRaw}
                    handleChangeText={(txt, type) => handleChangeTextEditor(txt, type)}
                  />
                </Box>
                <Divider />
                <Box mt={2} ml={2}>
                  <Typography variant="h3">Number of characters*</Typography>
                </Box>
                <Box ml={2} mb={2} mr={2}>
                  <TextField
                    id="outlined-multiline-static"
                    variant="outlined"
                    label="Maximum"
                    placeholder="Maximum"
                    className={classes.textFieldStyle}
                    value={maxCharEdit}
                    onChange={(e) => {
                      setCheckError(false);
                      setMaxCharEdit(e.target.value);
                      (currentInputLength &&
                      currentInputLength < parseInt(e.target.value))
                      && setRemainedChars(parseInt(e.target.value) - currentInputLength)
                    }}
                    fullWidth
                    helperText={
                      (!maxCharEdit || (checkError && maxCharEdit.toString().trim() === ""))
                      && "This field cannot be blank."
                    }
                    error={!maxCharEdit || (checkError && maxCharEdit.toString().trim() === "")}
                  />
                  {checkError &&
                    maxCharEdit.toString().trim() !== "" &&
                    ( isNaN(parseInt(maxCharEdit)) ||
                      parseInt(maxCharEdit) < 151 ||
                      parseInt(maxCharEdit) > 5000) && (
                      <FormHelperText className={classes.errorStyle} error>
                        Enter a number in the range of 151 to 5000.
                      </FormHelperText>
                    )
                  }
                </Box>
                <Box mt={2} ml={2}>
                  <Typography variant="h3">Width</Typography>
                </Box>
                <Box ml={2} mb={2} mr={2}>
                  <TextField
                    id="outlined-multiline-static"
                    variant="outlined"
                    label="Width"
                    placeholder="Width"
                    className={classes.textFieldStyle}
                    value={widthEdit}
                    onChange={(e) => {
                      setCheckError(false);
                      setWidthEdit(e.target.value);
                    }}
                    fullWidth
                    helperText={
                      checkError && widthEdit.toString().trim() === "" &&
                      "This field cannot be blank."
                    }
                    error={checkError && widthEdit.toString().trim() === ""}
                  />
                  {checkError &&
                    widthEdit.toString().trim() !== "" &&
                    ( isNaN(parseInt(widthEdit)) ||
                      parseInt(widthEdit) < 30 ||
                      parseInt(widthEdit) > 50) && (
                      <FormHelperText className={classes.errorStyle} error>
                        Enter a number in the range of 30 to 50.
                      </FormHelperText>
                    )
                  }
                </Box>
                <Box mt={2} ml={2}>
                  <Typography variant="h3">Height</Typography>
                </Box>
                <Box ml={2} mb={2} mr={2}>
                  <TextField
                    id="outlined-multiline-static"
                    variant="outlined"
                    label="Height"
                    placeholder="Height"
                    className={classes.textFieldStyle}
                    value={heightEdit}
                    onChange={(e) => {
                      setCheckError(false);
                      setheightEdit(e.target.value);
                    }}
                    fullWidth
                    helperText={
                      checkError && heightEdit.toString().trim() === "" &&
                      "This field cannot be blank."
                    }
                    error={checkError && heightEdit.toString().trim() === ""}
                  />
                  {checkError &&
                    heightEdit.toString().trim() !== "" &&
                    ( isNaN(parseInt(heightEdit)) ||
                      parseInt(heightEdit) < 3 ||
                      parseInt(heightEdit) > 6) && (
                      <FormHelperText className={classes.errorStyle} error>
                        Enter a number in the range of 3 to 6.
                      </FormHelperText>
                    )
                  }
                </Box>
                <Divider />
                <Box
                  mt={2}
                  ml={2}
                  mr={1}
                  mb={2}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="h3">Answer is Mandatory</Typography>
                  <Switch
                    checked={manAnswerEdit}
                    onChange={() => setManAnswerEdit(!manAnswerEdit)}
                    className={classes.switch}
                  />
                </Box>
                <Divider />
                <Box
                  mt={2}
                  ml={2}
                  mr={1}
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="h3">Enable Information Icon</Typography>
                  <Switch
                    checked={infoIconEdit}
                    onChange={() => {
                      setInfoIconEdit(!infoIconEdit);
                      if (!infoIconEdit) setFocusInfo(true);
                      infoIconEdit && setInfoTxtEdit("");
                    }}
                    className={classes.switch}
                  />
                </Box>
                <Box ml={2} mb={2} mr={2}>
                  <TextField
                    id="outlined-multiline-static"
                    variant="outlined"
                    inputRef={input => {
                      if (focusInfo && input != null) {
                        input.focus();
                      }
                    }}
                    inputProps={{
                      onBlur: () => {
                        setFocusInfo(false);
                      },
                    }}
                    placeholder="Type tooltip label"
                    value={infoTxtEdit}
                    disabled={!infoIconEdit}
                    onChange={e => setInfoTxtEdit(e.target.value)}
                    className={classes.textFieldStyle}
                    fullWidth
                    helperText={
                      checkError &&
                      infoIconEdit &&
                      infoTxtEdit.trim() === "" &&
                      "This field cannot be blank."
                    }
                    error={checkError && infoIconEdit && infoTxtEdit.trim() === ""}
                  />
                </Box>
                <Box
                  mt={5}
                  ml={2}
                  //  mb={3}
                  style={{
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.buttonStyle}
                    style={{ marginRight: 2 }}
                    onClick={() => {
                      if (!validateFields()) return;
                      handleDone(
                        "Text Area",
                        "done",
                        infoIconEdit,
                        manAnswerEdit,
                        queTextEdit,
                        queRawEdit,
                        capTextEdit,
                        capRawEdit,
                        maxCharEdit,
                        infoTxtEdit,
                        null,
                        null,
                        null,
                        widthEdit,
                        heightEdit,
                        true
                      );
                    }}
                  >
                    Save
                  </Button>
                  <Button
                    variant="text"
                    color="primary"
                    className={classes.outlinedButtonStyle}
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item lg={8} md={8} xl={8}>
            <Card style={{ backgroundColor: "#E3F2FD" }}>
              <Box
                mt={6}
                mb={1.5}
                ml={10}
                mr={10}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <Box style={{ display: "flex", flexDirection: "row" }}>
                    {infoIconEdit && infoTxtEdit ? (
                      <StyledTooltip title={infoTxtEdit}>
                        <img
                          style={{
                            paddingBottom: 3,
                            paddingRight: 5,
                            height: 24,
                          }}
                          src={infoImg}
                        />
                      </StyledTooltip>
                    ) : infoIconEdit && !infoTxtEdit ? (
                      <img
                        style={{
                          paddingBottom: 3,
                          paddingRight: 5,
                          height: 24,
                        }}
                        src={infoImg}
                      />
                    ) : (
                      ""
                    )}
                  </Box>
                  <Typography
                    variant="h3"
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        wordBreak: "break-word",
                      }}
                    >
                      {queTextEdit ? queTextEdit : ""}
                    </div>
                  </Typography>
                </Box>
                <Typography
                  variant="h3"
                  style={{
                    fontSize: 14,
                    display: "flex",
                    alignItems: "center",
                    paddingLeft: 3,
                    paddingBottom: 5,
                    paddingTop: 20,
                  }}
                >
                  <div
                    style={{
                      wordBreak: "break-word",
                    }}
                  >
                    {capTextEdit ? capTextEdit : ""}
                  </div>
                </Typography>

                <Box pb={0.25} style={{ display: "flex", flexDirection: "row" }}>
                  <Typography
                    variant="h3"
                    style={{
                      fontSize: 14,
                      display: "flex",
                      alignItems: "center",
                      paddingLeft: 5,
                    }}
                  >
                    Answer
                  </Typography>
                  {manAnswerEdit && (
                    <StyledTooltip title="required field">
                      <Typography
                        variant="h3"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          color: "#E53935",
                          paddingLeft: 5,
                          paddingRight: 5,
                        }}
                      >
                        *
                      </Typography>
                    </StyledTooltip>
                  )}
                </Box>
                <Box style={{ width: "fit-content" }}>
                  <TextField
                    id="outlined-multiline-static"
                    variant="outlined"
                    multiline
                    rows={6}
                    className={classes.textFieldOnFormStyle}
                    InputProps={{
                      classes: {
                         input: classes.textFieldInput
                      },
                    }}
                    onChange={e => updRemainedChars(e.target.value)}
                  />
                </Box>
                <Typography
                  variant="h6"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    paddingRight: 5,
                    paddingTop: 5,
                    color: "#00000099",
                  }}
                >
                  {maxCharEdit && !isNaN(parseInt(maxCharEdit))
                    ?
                      (remainedChars !== null && remainedChars > 0)
                      ?
                        remainedChars +
                        " of " + maxCharEdit + " Character(s) left"
                      : currentInputLength < parseInt(maxCharEdit)
                        ?
                          maxCharEdit + " of " + maxCharEdit + " Character(s) left"
                        :
                        ""
                    :
                    ""
                  }
                </Typography>
              </Box>
            </Card>
            {alertMsg.msg && (
              <AlertDialog
                title={alertMsg.title}
                type={alertMsg.type}
                contentMsg={alertMsg.msg}
                closeDialog={() => setAlertMsg([])}
                cancelSaveAction={() => {
                  setAlertMsg([]);
                  handleClose();
                }}
              />
            )}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
